<template>
  <div class="date-filters-container">
    <div class="date-filters-buttons-container">
      <button v-if="showAll" class="btn btn-primary"
        :class="{ active: filterType === 'all' }"
        @click="setFilter('all')">All</button>
      <button v-if="showDay" class="btn btn-primary"
        :class="{ active: filterType === 'day' }"
        @click="setFilter('day')">Today</button>
      <button v-if="showWeek" class="btn btn-primary"
        :class="{ active: filterType === 'week' }"
        @click="setFilter('week')">This Week</button>
      <button v-if="showCurrentMonth" class="btn btn-primary"
        :class="{ active: isCurrentMonthSelected }"
        @click="setToCurrentMonth">This Month</button>
      <button v-if="showNextTwoWeeks" class="btn btn-primary"
        :class="{ active: filterType === 'next-two-weeks' }"
        @click="setFilter('next-two-weeks')">Next 14 Days</button>
      <button v-if="showNextMonth" class="btn btn-primary"
        :class="{ active: filterType === 'next-month' }"
        @click="setFilter('next-month')">Next 30 Days</button>
      <select v-if="showMonth" class="btn btn-primary"
        :class="{ active: filterType === 'month' }"
        :value="selectedMonth"
        id="month-filter-select"
        @change="onMonthChange"
      >
        <option
          v-for="(label, index) in monthOptions"
          :key="index"
          :value="index"
        >
          {{ label }}
        </option>
      </select>
      <select v-if="showYear" class="btn btn-primary"
        :class="{ active: filterType === 'year' }"
        :value="selectedYear"
        id="year-filter-select"
        @change="onYearChange"
      >
        <option :value="''">Any year</option>
        <option
          v-for="year in yearOptions"
          :key="year"
          :value="year">
          {{ year }}
        </option>
      </select>
    </div>
    <div class="date-filters-selected">
      {{ dateRangeMessage }}
    </div>
  </div>

</template>

<style scoped>
.date-filters-buttons-container {
  display: flex;
  gap: 5px;
  margin: 15px 0;
}

.btn-primary {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.btn-primary:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.btn-primary:not(:last-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.btn-primary.active {
  background-color: var(--kate-background);
  border-color: var(--kate-background);
  color: var(--kate-type-white);
}

.date-filters-selected {
  margin: 10px 0;
  font-size: 14px;
  color: var(--kate-type-secondary);
}

</style>

<script>
import TimeMixin from '@mixins/time-mixins';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default {
  props: {
    showDay: {
      type: Boolean,
      default: true,
    },
    showWeek: {
      type: Boolean,
      default: true,
    },
    showMonth: {
      type: Boolean,
      default: true,
    },
    showCurrentMonth: {
      type: Boolean,
      default: true,
    },
    showNextTwoWeeks: {
      type: Boolean,
      default: false,
    },
    showNextMonth: {
      type: Boolean,
      default: false,
    },
    showAll: {
      type: Boolean,
      default: true,
    },
    showYear: {
      type: Boolean,
      default: true,
    },
  },

  mixins: [TimeMixin],

  data() {
    return {
      monthOptions: monthNames,
      selectedMonth: new Date().getMonth(),
      filterType: 'all',
      selectedYear: '',
      yearOptions: Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - 1 + i),
    };
  },

  computed: {
    currentMonth() {
      return new Date().getMonth();
    },
    currentYear() {
      return new Date().getFullYear();
    },
    isCurrentMonthSelected() {
      return this.filterType === 'month' && this.selectedMonth === this.currentMonth;
    },
    dateRangeMessage() {
      if (this.filterType === 'all' || !this.filterWindow || !this.filterWindow.start || !this.filterWindow.end) {
        return 'Showing all results';
      }
      if (this.filterType === 'day') {
        return `Showing results for today: ${this.formatDateString(this.filterWindow.start)}`;
      }
      if (this.filterType === 'month') {
        return `Showing results in ${this.formatDateString(this.filterWindow.start)} - ${this.formatDateString(this.filterWindow.end)}`;
      }
      return `Showing results in range ${this.formatDateString(this.filterWindow.start)} - ${this.formatDateString(this.filterWindow.end)}`;
    },

    filterWindow() {
      const today = new Date();

      if (this.filterType === 'day') {
        const start = new Date(today);
        start.setHours(0, 0, 0, 0);

        const end = new Date(today);
        end.setHours(23, 59, 59, 999);

        return { start, end };
      }
      if (this.filterType === 'week') {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay()); // Sunday
        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // Saturday
        weekEnd.setHours(23, 59, 59, 999);

        return { start: weekStart, end: weekEnd };
      }
      if (this.filterType === 'month') {
        const year = (this.selectedYear === '' || this.selectedYear === null)
          ? today.getFullYear()
          : this.selectedYear;
        const start = new Date(year, this.selectedMonth, 1);
        start.setHours(0, 0, 0, 0);

        const end = new Date(year, this.selectedMonth + 1, 0);
        end.setHours(23, 59, 59, 999);

        return { start, end };
      }
      if (this.filterType === 'next-two-weeks') {
        const start = new Date(today);
        start.setHours(0, 0, 0, 0);

        const end = new Date(start);
        end.setDate(start.getDate() + 14);
        end.setHours(23, 59, 59, 999);

        return { start, end };
      }
      if (this.filterType === 'next-month') {
        const start = new Date(today);
        start.setHours(0, 0, 0, 0);

        const end = new Date(start);
        end.setDate(start.getDate() + 30);
        end.setHours(23, 59, 59, 999);

        return { start, end };
      }
      if (this.filterType === 'year') {
        if (this.selectedYear === '' || this.selectedYear === null) {
          return { start: null, end: null };
        }
        const start = new Date(this.selectedYear, 0, 1); // selected year start
        start.setHours(0, 0, 0, 0);

        const end = new Date(this.selectedYear, 11, 31); // selected year end
        end.setHours(23, 59, 59, 999);

        return { start, end };
      }
      return { start: null, end: null };
    },
  },

  methods: {
    setFilter(type) {
      if (type !== 'month' && type !== 'year') {
        this.selectedMonth = this.currentMonth;
      }
      if (type === 'day' || type === 'week') {
        this.selectedYear = '';
      }
      if (type === 'all') {
        this.selectedYear = '';
        this.selectedMonth = this.currentMonth;
      }
      this.filterType = type;
      this.$emit('filter-window-changed', this.filterWindow);
    },

    onMonthChange(event) {
      this.selectedMonth = Number(event.target.value);
      this.filterType = 'month';
      this.$emit('filter-window-changed', this.filterWindow);
    },

    onYearChange(event) {
      const value = event.target.value;
      this.selectedYear = value === '' ? '' : Number(value);
      if (this.filterType === 'month') {
        this.$emit('filter-window-changed', this.filterWindow);
      } else {
        this.setFilter('year');
      }
    },

    setToCurrentYear() {
      this.selectedYear = new Date().getFullYear();
      this.setFilter('year');
    },

    setToCurrentMonth() {
      this.selectedMonth = this.currentMonth;
      this.setFilter('month');
    },
  },
};
</script>
